import { useLayoutEffect, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';

/**
 * Card — the one summary card, ported from views/components/stat-card.ejs.
 *
 * The card owns the box, the label's type and the figure's place; a page never writes those. `.card`
 * carries `.label`/`.value` styling from public/app.css, and `.card:has(.info-tip)` is what anchors a
 * tip in the corner — so `tip` must be rendered INSIDE the card, not beside it.
 */
export type CardProps = {
  /** The caption. May carry markup (the full/short label spans). */
  label?: ReactNode;
  /** Markup appended INSIDE the label — the dashboard's day-change ⓘ sits there. */
  labelTip?: ReactNode;
  /** The figure; the page formats money/percentages itself. */
  value?: ReactNode;
  /** 'strong' for the tax/harvest panels, which are not a page's headline numbers. */
  valueTag?: 'div' | 'strong';
  /** pos / neg, when the figure's sign carries meaning. */
  valueClass?: string;
  /** An id the page updates in place (the metrics cards). */
  valueId?: string;
  /** Plain-text tooltip for the figure. */
  title?: string;
  /** A block under the figure (a "still due" line, a caption). */
  note?: ReactNode;
  /** Stable identity for the card in its row (the Taxes page lets the reader reorder them). */
  cardKey?: string;
  /** Focusable so a keyboard user can move the card (Alt+Arrow on the Taxes page). */
  tabIndex?: number;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  /** A tip appended inside the card — the page's content, the card's box. */
  tip?: ReactNode;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
};

export function Card({
  label,
  labelTip,
  value,
  valueTag = 'div',
  valueClass,
  valueId,
  title,
  note,
  cardKey,
  tabIndex,
  onKeyDown,
  tip,
  className,
  style,
  children,
}: CardProps) {
  const Tag = valueTag === 'strong' ? 'strong' : 'div';
  return (
    <div
      className={className ? `card ${className}` : 'card'}
      style={style}
      data-card-key={cardKey}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
    >
      <div className="label">
        {label}
        {labelTip}
      </div>
      <Tag className={valueClass ? `value ${valueClass}` : 'value'} id={valueId} title={title}>
        {value ?? ''}
      </Tag>
      {note}
      {tip}
      {children}
    </div>
  );
}

/** Cards — a row of summary cards. The row is the component's; each child is a Card. */
export function Cards({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  const row = useRef<HTMLDivElement | null>(null);

  // The EJS pages shrink the one value that does not fit its card (`fitCardValues` in views/layout.ejs): it
  // measures `.value`'s scrollWidth against the card's inner width and writes `transform: scale(...)`. That
  // is a DOM pass, not CSS, and it lives in the EJS document — so the SPA, which loads app.css and nothing
  // else, drew every value at a flat 22px and the longest one (the day-change figure) read out of step with
  // the same row on the EJS page. The pass belongs to the component, where both front ends get it.
  useLayoutEffect(() => {
    const root = row.current;
    if (!root) return;
    const fit = () => {
      // `alignCardLabels`, ported from views/layout.ejs: if ANY label in a visual row wraps to two lines, every
      // label in that row takes a 2-line min-height so the figures under them line up. Without it the narrow
      // rows drew 6px shorter than the EJS page's and every card below a wrapped label rode up with its value.
      const labels = [...root.querySelectorAll<HTMLElement>('.card .label')];
      labels.forEach((l) => {
        l.style.minHeight = '';
        l.style.display = 'block';
      });
      if (labels.length) {
        const rowTop = root.getBoundingClientRect().top;
        const byRow = new Map<number, HTMLElement[]>();
        labels.forEach((l) => {
          const key = Math.round(l.getBoundingClientRect().top - rowTop);
          byRow.set(key, [...(byRow.get(key) ?? []), l]);
        });
        byRow.forEach((rowLabels) => {
          if (rowLabels.some((l) => l.getBoundingClientRect().height > 20)) {
            rowLabels.forEach((l) => {
              l.style.minHeight = '34px';
            });
          }
        });
      }
      root.querySelectorAll<HTMLElement>('.card').forEach((card) => {
        const v = card.querySelector<HTMLElement>('.value');
        if (!v) return;
        v.style.transform = 'none';
        v.style.display = '';
        const avail = card.clientWidth - 40; // the card's horizontal padding, as layout.ejs measures it
        if (avail <= 0) return;
        const natural = v.scrollWidth;
        if (natural <= avail) return;
        v.style.transform = `scale(${avail / natural})`;
        // scale() does not change layout width; inline-block keeps the scaled text left-aligned.
        v.style.display = 'inline-block';
      });
    };
    fit();
    // A webfont that lands after first paint changes the measurement, and so does a resize.
    void document.fonts?.ready.then(fit);
    window.addEventListener('resize', fit);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    ro?.observe(root);
    return () => {
      window.removeEventListener('resize', fit);
      ro?.disconnect();
    };
  }, [children]);

  return (
    <div className={className ? `cards ${className}` : 'cards'} style={style} ref={row}>
      {children}
    </div>
  );
}
